import { createContext, useContext, type ReactNode } from 'react';
import { useJsApiLoader, type Libraries } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from 'config/google-maps';

// ==============================|| GOOGLE MAPS CONTEXT ||============================== //

interface GoogleMapsContextValue {
  isLoaded: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextValue>({ isLoaded: false });

/**
 * Loads the Google Maps JS SDK once at the app level.
 * All child components consume `isLoaded` via useGoogleMaps() hook
 * instead of calling useJsApiLoader() individually.
 */
export function GoogleMapsProvider({ children }: { children: ReactNode }) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
    // config/google-maps.ts's `libraries` is inferred as string[], not the literal Libraries
    // union — this file is the first to actually type-check the call (untyped before this
    // conversion). 'places' is a genuine, valid Library value, not invented.
    libraries: GOOGLE_MAPS_CONFIG.libraries as Libraries,
    language: GOOGLE_MAPS_CONFIG.language,
    region: GOOGLE_MAPS_CONFIG.region
  });

  return <GoogleMapsContext.Provider value={{ isLoaded }}>{children}</GoogleMapsContext.Provider>;
}

/**
 * Hook to access Google Maps SDK load state.
 */
export const useGoogleMaps = () => useContext(GoogleMapsContext);
